/**
 * Утилиты форматирования содержимого ячеек QueryTableLite (cell-items-lite).
 *
 * Совпадают по поведению с `SimpleCellElement.getElements`,
 * `IconCellElement.getElements`, `ChangeDateCellElement.getElements`,
 * `BillCellElement.getElements`, но без Vue-обёрток и Quasar-компонентов.
 *
 * Импортируются и cell-items-lite, и QTLiteRow для inline-рендера.
 */

import quasarDate from 'quasar/src/utils/date/date.js';;
import { customIsDate } from "@/utils/auxfunc";
import { isPhoneNumber } from "@/utils/contactsHelpers";
import {
  PrimitiveCellValueType,
  TableDataCellDisplayType,
  V2StorageItemMovementType,
} from "@/models/etc/types";
import type {
  CellDataBill,
  CellDataLastChangeDate,
  CellDataMovementsOfItem,
  CellDataMovementType,
  CellDataSimple,
  TableDataCell,
} from "@/models/interfaces/entity-settings/querytable/querytable";

/**
 * Единый формат числа в lite-таблице: группировка целой части по тройкам пробелом
 * (1 000, 25 000, 1 250 000) и математическое округление (half away from zero) до
 * 2 знаков (2.345 -> 2.35). Разделитель дробной части - точка, хвостовые нули
 * убираем (2.50 -> 2.5, 2.00 -> 2). Нечисло возвращаем строкой как есть.
 */
export function formatNumber(value: unknown): string {
  const n = typeof value === "number" ? value : Number(value);
  if (!isFinite(n)) return String(value ?? "");
  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);
  // округление через строковую экспоненту, чтобы не ловить float-погрешность вида 2.345*100
  const rounded = Number(`${Math.round(Number(`${abs}e2`))}e-2`);
  let intPart = Math.trunc(rounded).toString();
  let fracPart = "";
  if (rounded - Math.trunc(rounded) > 0) {
    fracPart = rounded.toFixed(2).split(".")[1].replace(/0+$/, "");
  }
  // группировка целой части пробелом каждые 3 разряда справа
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return sign + intPart + (fracPart ? "." + fracPart : "");
}

/** Простые типы, Date / DateTime / Number / Boolean / String. Совпадает с SimpleCellElement.getElements. */
export function formatSimpleValue(cell: CellDataSimple, displayType: TableDataCellDisplayType, t: (k: string) => string): string {
  // Прочерк для пустого значения оставляем ТОЛЬКО в параметрах; во всех остальных
  // типах ячеек пустое значение - это пустая строка. 0 - валидное значение и всегда
  // отображается как "0" (не пусто и не прочерк). Пустым считаем только null/undefined
  // и пустую строку; ни 0→пусто, ни пусто→0 не фолбечим.
  const isParam = displayType === TableDataCellDisplayType.Parameter;
  const emptyText = isParam ? " - " : "";
  let value: string;
  switch (cell.ValueType) {
    case PrimitiveCellValueType.Date: {
      value = quasarDate.formatDate(new Date(Date.parse("" + cell.Value)), "DD.MM.YYYY");
      value = `<span class="no-break">${value}</span>`;
      break;
    }
    case PrimitiveCellValueType.DateTime: {
      value = quasarDate.formatDate(new Date(Date.parse("" + cell.Value)), "DD.MM.YYYY HH:mm:ss");
      const [datePart, timePart] = value.split(" ");
      value = `<span class="no-break">${datePart}</span> <span class="no-break">${timePart}</span>`;
      break;
    }
    case PrimitiveCellValueType.Number: {
      // пустое значение -> emptyText (прочерк только для параметра, иначе пусто);
      // 0 - валидное число и всегда показывается как "0".
      const empty = cell.Value === null || cell.Value === undefined || (cell.Value as unknown) === "";
      value = empty ? emptyText : formatNumber(cell.Value);
      value = `<span class="no-break">${value}</span>`;
      break;
    }
    case PrimitiveCellValueType.Boolean: {
      // null/undefined/"" - это "не выбрано" (значение не задано), а не false.
      // Для boolean пустое показываем текстом "Не выбрано"; да/нет - только для
      // реально заданного значения.
      const raw = cell.Value as unknown;
      const empty = raw === null || raw === undefined || (raw as unknown) === "";
      value = empty ? t("not_selected") : (raw ? t("yes_label") : t("no_label"));
      break;
    }
    default: {
      // В строковом значении с сервера прилетает подсветка совпадений поиска:
      // класс searchedText и жёлтый фон через inline background-color. Сам span и
      // структуру оставляем, вычищаем и класс, и фоновую подсветку - в таблице она
      // не нужна и мозолит глаза (видно, например, в имени контакта).
      // Пустое (null/undefined/"") -> emptyText; 0 (в т.ч. строкой) остаётся как есть.
      const raw = cell.Value as unknown;
      const empty = raw === null || raw === undefined || raw === "";
      value = empty ? emptyText : stripSearchHighlight("" + raw);
    }
  }
  // Префикс "#" у тега не добавляем к пустому значению, чтобы пустая ячейка не
  // превращалась в одинокий "#".
  if (displayType === TableDataCellDisplayType.SimpleAsTag) {
    return value ? "#" + value : value;
  }
  return value;
}

/**
 * Убирает серверную подсветку совпадений поиска из HTML-строки значения:
 *   • класс `searchedText` из class-атрибутов;
 *   • фоновую подсветку (`background-color` / шорткат `background`) из inline-стилей.
 * Сам тег и структуру HTML сохраняем: из class/style вырезаем только эти токены, а
 * если атрибут после этого пустой - удаляем его целиком. Остальные классы и стили
 * (например, background-image) не трогаем.
 */
export function stripSearchHighlight(html: string): string {
  if (!html) return html;
  let out = html;

  // класс searchedText
  if (out.indexOf("searchedText") !== -1) {
    out = out.replace(/\s+class=(["'])([^"']*)\1/gi, (_m, quote, classes) => {
      const kept = classes
        .split(/\s+/)
        .filter((c: string) => c && c !== "searchedText")
        .join(" ");
      return kept ? ` class=${quote}${kept}${quote}` : "";
    });
  }

  // фоновая подсветка в inline-стиле
  if (/background/i.test(out)) {
    out = out.replace(/\s+style=(["'])([^"']*)\1/gi, (_m, quote, style) => {
      const kept = style
        .split(";")
        .map((d: string) => d.trim())
        .filter((d: string) => d && !/^background(-color)?\s*:/i.test(d))
        .join("; ");
      return kept ? ` style=${quote}${kept}${quote}` : "";
    });
  }

  return out;
}

/** HTML-контент для DisplayType 0/6, конкатенация всех Data через <br/>. */
export function formatSimpleHtml(cell: TableDataCell, t: (k: string) => string): string {
  return cell.Data
    .map((d) => formatSimpleValue(d as CellDataSimple, cell.DisplayType, t))
    .join("<br/>");
}

/** Текст для DisplayType 7 (LastChangeDate), массив строк "ИмяАвтора DD.MM.YYYY HH:mm:ss". */
export function formatChangeDateRows(cell: TableDataCell): string[] {
  return (cell.Data as CellDataLastChangeDate[]).map((d) => {
    const namePart = d.LastChanger ? d.LastChanger.Name : "";
    const datePart = d.LastChangeDate
      ? " " + quasarDate.formatDate(new Date(d.LastChangeDate), "DD.MM.YYYY HH:mm:ss")
      : "";
    return namePart + datePart;
  });
}

/** Bill: массив строковых представлений Bill (просто.Bill, оригинал JSON.stringify фолбэк). */
export function formatBillRows(cell: TableDataCell): string[] {
  if (cell.DisplayType === TableDataCellDisplayType.Bill) {
    return (cell.Data as CellDataBill[]).map((d) => String(d.Bill ?? ""));
  }
  return [JSON.stringify(cell.Data)];
}

/**
 * Универсальный preview cell.Data → HTML-строка для measure-фазы.
 *
 * Зачем HTML, а не plain text: каждый «неразрывный» элемент (число, дата,
 * имя сущности, тег) оборачивается в <span class="no-break"> с
 * `white-space: nowrap`. Это нужно, чтобы браузер при table-layout:auto
 * считал min-content колонки = ширина САМОГО ДЛИННОГО НЕРАЗРЫВНОГО ЭЛЕМЕНТА
 * (а не самого длинного слова, иначе колонке с числом «2512083.84» дадут
 * ширину последнего слова препдыдущего значения, и в production число
 * вылезет за границу).
 *
 * Число и дата всегда обёрнуты в `<span class="no-break">`, тогда колонка получает
 * достаточную ширину.
 *
 * Используется только в measure-фазе (через v-html), в production-рендере
 * применяются реальные `*Lite`-компоненты.
 */
export function formatPreviewText(cell: TableDataCell): string {
  if (!cell || !cell.Data || !cell.Data.length) return "";
  return cell.Data
    .map((d: any) => previewToken(d))
    .filter(Boolean)
    .join(", ");
}

/**
 * Один элемент Data → HTML-фрагмент.
 *
 * КРИТИЧНО: <span class="no-break"> применяем ТОЛЬКО к атомарным значениям,
 * которые в принципе нельзя/не нужно переносить:
 *   • число
 *   • дата / время
 *   • Bill.Number, Quantity
 *   • короткие технические идентификаторы (… (N))
 *
 * А имена сущностей, теги, job.Name и Entity.Name возвращаем КАК ЕСТЬ: это длинные
 * фразы, они должны переноситься по словам. В no-break их оборачивать нельзя, иначе
 * min-content колонки станет равен ширине всей фразы, такие колонки распирают таблицу
 * и расчёт ширин ломается: колонка «Клиент» займёт пол-экрана, остальные сожмутся
 * до трёх букв.
 */
function previewToken(d: any): string {
  if (!d) return "";
  if (d.Entity?.Name) return escapeText(d.Entity.Name);
  if (d.Tag?.Name) return escapeText(d.Tag.Name);
  if (d.Bill?.Number !== undefined) return wrapNoBreak(d.Bill.Number);
  if (d.Bill !== undefined && typeof d.Bill !== "object") return wrapNoBreak(d.Bill);
  if (d.Job?.Name) return escapeText(d.Job.Name);
  if (d.JobName) return escapeText(d.JobName);
  if (d.JobStatus !== undefined) return escapeText(d.JobStatus);
  if (d.LastChanger?.Name && d.LastChangeDate) {
    return escapeText(d.LastChanger.Name) + " " + wrapNoBreak(formatDateShort(d.LastChangeDate));
  }
  if (d.LastChangeDate) return wrapNoBreak(formatDateShort(d.LastChangeDate));
  if (Array.isArray(d.IdList)) return wrapNoBreak(`… (${d.IdList.length})`);
  if (d.Storage?.Name && d.Quantity !== undefined) {
    return escapeText(d.Storage.Name) + ": " + wrapNoBreak(d.Quantity);
  }
  if (d.MeasureUnit?.Name && d.Quantity !== undefined) {
    return wrapNoBreak(d.Quantity) + " " + escapeText(d.MeasureUnit.Name);
  }
  if (d.Value !== undefined && d.Value !== null) {
 // Для DateTime/Date значение приходит как ISO-строка, форматируем как в production.
    if (typeof d.Value === "string" && /^\d{4}-\d{2}-\d{2}/.test(d.Value)) {
      return wrapNoBreak(formatDateShort(d.Value));
    }
 // Числа всегда оборачиваем, иначе при table-layout:fixed выезжают в production.
 // Формат тот же, что в production (formatNumber), чтобы ширины мерялись точно.
    if (typeof d.Value === "number") return wrapNoBreak(formatNumber(d.Value));
 // Численная строка, тоже nowrap (ID, code, etc).
    if (typeof d.Value === "string" && /^[\d.,\s+-]+$/.test(d.Value)) return wrapNoBreak(d.Value);
    return escapeText(d.Value);
  }
  if (d.Name) return escapeText(d.Name);
  return "";
}

/** Только escape, без no-break, для wrap-able текстовых полей. */
function escapeText(value: unknown): string {
  return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function wrapNoBreak(value: unknown): string {
  // Минимальный escape: не используем сложные значения здесь, но защитим от <>.
  const s = String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return `<span class="no-break">${s}</span>`;
}

function formatDateShort(value: unknown): string {
  try {
    const d = new Date(typeof value === "string" || typeof value === "number" ? value : String(value));
    if (isNaN(d.getTime())) return String(value ?? "");
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}.${mm}.${yyyy}`;
  } catch {
    return String(value ?? "");
  }
}

/* Регулярка телефонных номеров. */
const PHONE_REGEX = /([+]?[0-9\s.-]*(?:\([0-9\s.-]+\))?[-.\s0-9]*[0-9])(?![0-9\s.-]*@)/gi;

/** Проверяем длину и префикс номера по общим правилам работ и контактов. */
export function cleanPhoneNumber(match: string): string | null {
  const value = match.trim();
  const hasPlus = value.startsWith("+");
  const digits = value.replace(/\D/g, "");
  if (!isPhoneNumber(digits, hasPlus)) return null;
  return (hasPlus ? "+" : "") + digits;
}

/**
 * Оборачивает телефонные номера в HTML-строке в кликабельный якорь без нативного
 * набора (без href="tel:"): по клику идёт бэкенд-запрос на звонок через callPhoneNumber,
 * а системный набор номера ОС не открывается. Работает только для контактных колонок
 * (isContact), иначе возвращает строку без изменений. Даты по customIsDate номерами не считаем.
 */
export function wrapPhones(html: string, isContact: boolean): string {
  if (!isContact || !html) return html;
  return html.replaceAll(PHONE_REGEX, (match) => {
    const cleaned = cleanPhoneNumber(match);
    if (!cleaned || customIsDate(match)) return match;
    return `<a class="phone-number" data-phone="${cleaned}">${match}</a>`;
  });
}

/** Icon (DisplayType 12/15), html-строки, повторяет IconCellElement.getElements 1:1. */
export function formatIconHtml(cell: TableDataCell): string[] {
  if (cell.DisplayType === TableDataCellDisplayType.MovementsOfItem) {
    return cell.Data.map((d) => {
      const item = d as CellDataMovementsOfItem;
      let value = quasarDate.formatDate(new Date(Date.parse("" + item.LastMovementDate)), "DD.MM.YY HH:mm");
      const [datePart, timePart] = value.split(" ");
      value = (item.DisplayCount ? item.MovementsCount + ", " : "") + `<span class="no-break">${datePart}</span>`;
      switch (item.MovementsType) {
        case V2StorageItemMovementType.In:
          value = `<span class="no-break movement-element movement-element--income">${value}</span>`; break;
        case V2StorageItemMovementType.Out:
          value = `<span class="no-break movement-element movement-element--expense">${value}</span>`; break;
        case V2StorageItemMovementType.Inner:
          value = `<span class="no-break movement-element movement-element--transfer">${value}</span>`; break;
      }
      return value + ` <span class="no-break">${timePart}</span>`;
    });
  }
  if (cell.DisplayType === TableDataCellDisplayType.MovementType) {
    return cell.Data.map((d) => {
      const item = d as CellDataMovementType;
      let value = "";
      switch (item.MovementType) {
        case V2StorageItemMovementType.In:
          value = `<span class="no-break movement-element movement-element--income">${value}</span>`; break;
        case V2StorageItemMovementType.Out:
          value = `<span class="no-break movement-element movement-element--expense">${value}</span>`; break;
        case V2StorageItemMovementType.Inner:
          value = `<span class="no-break movement-element movement-element--transfer">${value}</span>`; break;
      }
      return value;
    });
  }
  return [JSON.stringify(cell)];
}
